'use client';

import { c as _c } from "react/compiler-runtime";
import { defaultPreLiquidationParamsRegistry, type MarketId } from '@morpho-org/blue-sdk';
import { toBigInt, ZERO } from '@morpho-org/dapp-core';
import { InfoTooltip } from '@morpho-org/ui-v6/client';
import { Card, CardText, Skeleton } from '@morpho-org/ui-v6/server';
import { cn, formatters } from '@morpho-org/ui-v6/utils';
import { Icon, Separator } from '@repo/ui';
import { ShieldCheckGradient } from '@repo/ui/icons';
import { formatLtv } from '@/helpers/formatters';
import { AUTO_DELEVERAGE_LIQ_LTV_TOOLTIP_CONTENT } from '../utils/constants';
import AutoDeleverageMarketInfos from './AutoDeleverageMarketInfos';
import { useAutoDeleverageDialogData } from './useAutoDeleverageDialogData';
interface AutoDeleverageDialogDetailsProps {
  marketId: MarketId;
  ltv: bigint | null;
  isPositionRisky: boolean;
}
const AutoDeleverageDialogDetails = t0 => {
  const $ = _c(33);
  const {
    marketId,
    ltv,
    isPositionRisky
  } = t0;
  const {
    data,
    error: marketQueryError,
    isPending: isMarketLoading
  } = useAutoDeleverageDialogData(marketId);
  const lltv = data?.lltv;
  const loanAsset = data?.loanToken;
  const collateralAsset = data?.collateralToken;
  let t1;
  if ($[0] !== lltv) {
    t1 = formatters.percent.digits(2).default("-").trailingZero(false).unit("%").of(toBigInt(lltv), 18);
    $[0] = lltv;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedLltv = t1;
  let t2;
  if ($[2] !== lltv) {
    t2 = formatters.percent.digits(2).default("-").trailingZero(false).unit("%").of(defaultPreLiquidationParamsRegistry.get(toBigInt(lltv) ?? ZERO)?.preLltv, 18);
    $[2] = lltv;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const formattedPreLltv = t2;
  let t3;
  if ($[4] !== ltv) {
    t3 = formatLtv(ltv, "0%");
    $[4] = ltv;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const formattedLtv = t3;
  if (isMarketLoading) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton className="h-[220px] rounded-[20px]" />;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  if (marketQueryError || !loanAsset || !collateralAsset) {
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <CardText text="An error occurred while loading the market data." className="bg-muted rounded-xl shadow-none" height="220px" />;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t4;
  if ($[8] !== collateralAsset || $[9] !== formattedLltv || $[10] !== loanAsset || $[11] !== marketId) {
    t4 = <AutoDeleverageMarketInfos collateralAsset={collateralAsset} loanAsset={loanAsset} formattedLltv={formattedLltv} marketId={marketId} />;
    $[8] = collateralAsset;
    $[9] = formattedLltv;
    $[10] = loanAsset;
    $[11] = marketId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== formattedLtv || $[14] !== isPositionRisky || $[15] !== ltv) {
    t5 = !!ltv && <><div className="flex items-center justify-between"><span className="text-secondary-foreground text-xs">Your LTV</span><span className={cn("text-title-xs", isPositionRisky ? "text-destructive-foreground" : "text-foreground")}>{formattedLtv}</span></div><Separator /></>;
    $[13] = formattedLtv;
    $[14] = isPositionRisky;
    $[15] = ltv;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="gap-2xs flex items-center"><span className="text-secondary-foreground text-xs" style={{
        lineHeight: "normal"
      }}>Auto Deleverage Liq. LTV</span><InfoTooltip tooltipContent={AUTO_DELEVERAGE_LIQ_LTV_TOOLTIP_CONTENT} /></div>;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon icon={<ShieldCheckGradient />} />;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      lineHeight: "normal"
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== formattedPreLltv) {
    t9 = <div className="flex items-center justify-between">{t6}<div className="flex items-center gap-[2px]">{t7}<span className="text-title-xs" style={t8}>{formattedPreLltv}</span></div></div>;
    $[20] = formattedPreLltv;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Separator />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="text-secondary-foreground text-xs">Liquidation LTV</span>;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== formattedLltv) {
    t12 = <div className="flex items-center justify-between">{t11}<span className="text-title-xs">{formattedLltv}</span></div>;
    $[24] = formattedLltv;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t5 || $[28] !== t9) {
    t13 = <div className="gap-sm flex w-[100%] flex-col">{t5}{t9}{t10}{t12}</div>;
    $[26] = t12;
    $[27] = t5;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13 || $[31] !== t4) {
    t14 = <Card padding="s" className="bg-muted rounded-xl shadow-none"><div className="gap-lg flex w-[100%] flex-col">{t4}{t13}</div></Card>;
    $[30] = t13;
    $[31] = t4;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
};
export default AutoDeleverageDialogDetails;